import { inject, Injectable, OnDestroy } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Router } from '@angular/router';
import {
    catchError,
    combineLatest,
    finalize,
    map,
    Observable,
    of,
    shareReplay,
    Subject,
    switchMap,
    throwError,
} from 'rxjs';
import { environment } from '@env/environment';
import { toSignal } from '@angular/core/rxjs-interop';
import { redirectToPortal } from '@services/portal-redirect';
import { SessionStorageService } from '@services/sessionstorage.service';
import { LOGOUT_CHANNEL, LOGOUT_MESSAGE_TYPE } from '@services/logout-broadcast';
import { isCallbackUrl } from '@services/tab-aware-auth';

export interface CurrentUser {
    keycloakId: string;
    username: string;
    email: string;
    fullName: string;
    roles: string[];
    isAdmin: boolean;
}

/** Адрес раздела, в который надо вернуться после входа. Свой для вкладки: токены общие, адрес — нет. */
const RETURN_URL_KEY = 'returnUrl';

/** Метка в sessionStorage вкладки-инициатора: когда нажали «Выход». */
const LOGOUT_MARKER_KEY = 'logout-started-at';

/** Сколько метка остаётся годной. Дольше — считаем её протухшей. */
const LOGOUT_MARKER_TTL_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
    private readonly oidc = inject(OidcSecurityService);
    private readonly router = inject(Router);
    private readonly sessionStorage = inject(SessionStorageService);

    readonly isAuthenticated$ = this.oidc.isAuthenticated$.pipe(
        map(({ isAuthenticated }) => isAuthenticated),
        shareReplay(1),
    );

    public readonly isAuthenticated = toSignal(this.isAuthenticated$);

    /**
     * Один BroadcastChannel на вкладку — и отправка, и приём через него.
     * Отправитель по спецификации не получает собственное сообщение, поэтому
     * вкладка, нажавшая «Выход», не перезагрузится, отменив переход на Keycloak.
     */
    private readonly logoutChannel = new BroadcastChannel(LOGOUT_CHANNEL);
    private readonly remoteLogout = new Subject<void>();

    /** «В другой вкладке вышли». Свои сообщения сюда не попадают. */
    readonly remoteLogout$ = this.remoteLogout.asObservable();

    /** Общий на все параллельные 401 запрос обновления токена, пока он идёт. */
    private inFlightRefresh$: Observable<string> | null = null;

    readonly currentUser$: Observable<CurrentUser | null> = combineLatest([
        this.oidc.userData$,
        this.oidc.getAccessToken(),
    ]).pipe(
        map(([{ userData }, accessToken]) => {
            if (!userData) return null;

            const tokenPayload = this.decodeTokenPayload(accessToken ?? '');
            const resourceAccess = tokenPayload?.resource_access || {};

            const spaClientId = environment.keycloak.clientId;
            const spaRoles: string[] = resourceAccess[spaClientId]?.roles || [];

            const apiClientId = environment.keycloak.apiClientId;
            const apiRoles: string[] = resourceAccess[apiClientId]?.roles || [];

            const currentUser = {
                keycloakId: userData['sub'] || '',
                username: userData['preferred_username'] || '',
                email: userData['email'] || '',
                fullName: userData['name'] || '',
                roles: [...new Set([...spaRoles, ...apiRoles])],
                isAdmin: apiRoles.includes('admin'),
            };

            return currentUser;
        }),
        shareReplay(1),
    );

    constructor() {
        this.logoutChannel.addEventListener('message', (event: MessageEvent) => {
            if (event.data?.type === LOGOUT_MESSAGE_TYPE) {
                this.remoteLogout.next();
            }
        });
    }

    ngOnDestroy(): void {
        this.logoutChannel.close();
    }

    /** Свежий access-токен или пустая строка. Кэшировать нельзя: значение одноразовое. */
    getAccessTokenNow(): string {
        let token = '';
        this.oidc
            .getAccessToken()
            .subscribe((value) => (token = value))
            .unsubscribe();
        return token;
    }

    /**
     * Уход на Keycloak с сохранением текущего адреса, чтобы после входа открылся тот же раздел.
     * Адрес кладём в sessionStorage: authorize() уводит в другое хранилище, из которого мы и вернёмся.
     *
     * Корневой адрес не сохраняем и уже сохранённый раздел не затираем: в корень вкладка
     * возвращается с end-session или authorize, и разделы там ещё не знает — затрём нужный.
     */
    loginSso(returnUrl?: string): void {
        this.rememberSection(returnUrl);
        this.oidc.authorize();
    }

    /** Сохранённый адрес возврата; читается один раз — при разборе адреса ключ удаляется. */
    takeReturnUrl(): string | null {
        const returnUrl = this.sessionStorage.getData<string | null>(RETURN_URL_KEY);
        if (!returnUrl) return null;

        this.sessionStorage.removeData(RETURN_URL_KEY);
        return returnUrl;
    }

    /** Запомнить раздел, из которого ушли: после входа вернёмся сюда, а не на главную. */
    private rememberSection(url?: string | null): void {
        if (!url || url === '/' || isCallbackUrl(url)) return;
        this.sessionStorage.saveData(RETURN_URL_KEY, url);
    }

    /**
     * Сессия Keycloak закончилась: уходим на вход с текущего адреса.
     *
     * Токены гасим до authorize: иначе authorize успеет молча перевыпустить их на месте
     * по ещё живому refresh-токену и вернуть пользователя в приложение без входа.
     */
    endSession(): void {
        const url = this.router.url;

        this.logoffLocal();
        this.loginSso(url);
    }

    /**
     * Разалогин: уводим на end-session Keycloak, а остальные вкладки оповещаем
     * позже — из completeLogout() на бут после возврата.
     *
     * Если крикнуть сразу, соседние вкладки перезагрузятся, пока SSO-сессия Keycloak ещё жива:
     * перезагрузившись без токена, они уйдут в authorize и молча получат новый — «Выход»
     * выглядел бы не сработавшим. Метка нужна и для id_token_hint: logoff() читает его
     * из хранилища, поэтому чужие вкладки не должны стирать токены до конца этого вызова.
     */
    logout(): void {
        // Раздел запоминаем до ухода: после выхода вкладка вернётся в корень, и без этого
        // следующий вход открыл бы главную, а не тот раздел, из которого вышли.
        this.rememberSection(this.router.url);
        this.sessionStorage.saveData(LOGOUT_MARKER_KEY, Date.now());
        this.oidc.logoff().subscribe({
            next: () => undefined,
            error: () => this.logoffLocal(),
        });
    }

    /**
     * Бут после возврата с end-session: SSO-сессия Keycloak уже уничтожена, можно
     * оповещать остальные вкладки — они перезагрузятся и уйдут на страницу входа,
     * а не вернутся в приложение.
     *
     * @returns true, если вкладка была инициатором выхода и сигнал отправлен.
     */
    completeLogout(): boolean {
        const startedAt = this.sessionStorage.getData<number | null>(LOGOUT_MARKER_KEY);
        if (!startedAt) return false;

        this.sessionStorage.removeData(LOGOUT_MARKER_KEY);
        // Метка из прошлого сеанса вкладки: сигнал давно неактуален.
        if (Date.now() - startedAt > LOGOUT_MARKER_TTL_MS) return false;

        this.logoffLocal();
        this.broadcastLogout();
        return true;
    }

    /**
     * Только локальный сброс токенов, без обращения к Keycloak.
     *
     * Сохранённый раздел возврата не трогаем: он и есть ответ на «куда вернуться после входа».
     */
    logoffLocal(): void {
        this.oidc.logoffLocal();
    }

    /**
     * Оповестить остальные вкладки: в них тоже нужно сбросить состояние и перезагрузиться.
     *
     * Вкладки одного приложения — first-party, сигнал между ними доходит. А вот из iframe,
     * который открыл Keycloak, не доходит: браузер разделяет хранилище между сайтом вкладки
     * и чужим сайтом, внутри которого открыт iframe.
     */
    broadcastLogout(): void {
        this.logoutChannel.postMessage({ type: LOGOUT_MESSAGE_TYPE });
    }

    /**
     * Принудительное обновление токена одно на все параллельные 401: первый уходит в сеть,
     * остальные подцепляются к тому же результату через shareReplay. Отдельный Subject с take(1)
     * для этого не годился — подписчик, пришедший после сигнала, ждал бы вечно.
     * finalize обнуляет поле на завершении потока, поэтому следующий 401 снова пробует
     * обновить токен сам.
     */
    refreshOnce(): Observable<string> {
        if (!this.inFlightRefresh$) {
            this.inFlightRefresh$ = this.oidc.forceRefreshSession().pipe(
                switchMap(() => this.oidc.getAccessToken()),
                switchMap((token) =>
                    token
                        ? of(token)
                        : // Токена после обновления нет: повтор не поможет, запрос ушёл бы с пустым
                          // Authorization и снова получил бы 401, уйдя в рекурсию.
                          throwError(() => new Error('Нет access-токена после обновления')),
                ),
                catchError((error) => {
                    this.handleRefreshFailure(error);
                    return throwError(() => error);
                }),
                finalize(() => (this.inFlightRefresh$ = null)),
                shareReplay({ bufferSize: 1, refCount: false }),
            );
        }

        return this.inFlightRefresh$;
    }

    private decodeTokenPayload(token: string): any {
        try {
            const parts = token.split('.');
            if (parts.length < 2) return {};
            let str = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            while (str.length % 4 !== 0) {
                str += '=';
            }
            return JSON.parse(atob(str));
        } catch (error) {
            console.error('Error decoding token payload:', error);
            return {};
        }
    }

    hasSpaAccess(token: string): boolean {
        const tokenPayload = this.decodeTokenPayload(token);
        const result = this.checkSpaAccessRole(tokenPayload);
        return result;
    }

    handleAccessDenied(): void {
        redirectToPortal('no-access');
    }

    /**
     * Обновление не вышло. 400/401 — Keycloak сессию больше не отдаёт, выходим на вход.
     * Всё прочее (обрыв сети, 403) на вход не отправляет: сессия может быть жива,
     * виноват канал связи или права.
     */
    private handleRefreshFailure(error: unknown): void {
        const message = error as { status?: unknown } | null;
        if (message?.status === 400 || message?.status === 401) {
            this.endSession();
        }
    }

    private checkSpaAccessRole(tokenPayload: any): boolean {
        const clientId = environment.keycloak.clientId;
        const resourceAccess = tokenPayload?.resource_access;

        if (!resourceAccess || !resourceAccess[clientId]) {
            return false;
        }
        const roles = resourceAccess[clientId]?.roles || [];
        const hasAccess = roles.includes('access');
        return hasAccess;
    }
}
